"use client";

/**
 * Root Error Boundary
 *
 * Catches errors in the root layout (header/footer data loading).
 * This is a fallback for catastrophic failures where the layout itself fails.
 *
 * Following CLAUDE.md:
 * - Brand-agnostic styling with Tailwind
 * - No hardcoded colors (use semantic tokens)
 */

import { useEffect } from "react";

interface GlobalErrorProps {
	error: Error & { digest?: string };
	reset: () => void;
}

export default function RootError({ error, reset }: GlobalErrorProps) {
	useEffect(() => {
		console.error("Global application error:", error);
	}, [error]);

	return (
		<html
			lang="en"
			data-brand={process.env.NEXT_PUBLIC_BRAND || "modus-property"}
		>
			<body className="min-h-screen flex items-center justify-center bg-background">
				<div className="max-w-2xl mx-auto px-4 text-center space-y-8">
					<div className="space-y-4">
						<h1 className="text-6xl font-bold text-destructive">500</h1>
						<h2 className="text-3xl font-semibold">System Error</h2>
						<p className="text-muted-foreground text-lg">
							A critical system error occurred. The site cannot be loaded at
							this time.
						</p>

						{process.env.NODE_ENV === "development" && (
							<details className="mt-4 text-left bg-muted p-4 rounded-md">
								<summary className="cursor-pointer font-semibold text-sm">
									Error Details (Development Only)
								</summary>
								<pre className="mt-2 text-xs overflow-auto">
									{error.message}
									{error.digest && `\n\nDigest: ${error.digest}`}
								</pre>
							</details>
						)}
					</div>

					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<button
							onClick={reset}
							className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-6 py-3 font-medium"
							type="button"
						>
							Try again
						</button>
						<a
							href="/"
							className="inline-flex items-center justify-center rounded-md bg-secondary text-secondary-foreground px-6 py-3 font-medium"
						>
							Return to homepage
						</a>
					</div>

					{error.digest && (
						<div className="text-sm text-muted-foreground">
							<p>Error digest:</p>
							<code className="text-xs bg-muted px-2 py-1 rounded">
								{error.digest}
							</code>
						</div>
					)}
				</div>
			</body>
		</html>
	);
}
